/**
 * Telefone, e-mail e documento editáveis no painel do cliente (13/10/2026).
 *
 * ── No lugar, e um campo por vez ──────────────────────────────────────────
 *
 * Quem atende descobre o e-mail ou o CPF no meio da conversa, e o cadastro
 * completo mora no Cliente 360, a uma tela de distância. Aqui o lápis ao lado
 * do valor troca a linha por um campo, e Enter grava — sem tirar a conversa da
 * vista. As regras são as do 360 (`cliente/cadastro.tsx`), pela mesma rota.
 *
 * ── O que se manda ────────────────────────────────────────────────────────
 *
 * Só o campo da linha. Telefone e e-mail esvaziados vão como `null`, que
 * apaga. O documento é diferente: ele não vem do servidor (é guardado
 * cifrado), o campo nasce vazio, e por isso vazio NÃO apaga — só não muda.
 *
 * ── Quem edita ────────────────────────────────────────────────────────────
 *
 * `cliente × editar` na matriz: vendedor, gestor e administrador. O atendente
 * só lê, e para ele o lápis não aparece — esconder, e não desabilitar, como a
 * lista de clientes faz com "Novo cliente". A API recusa de qualquer forma.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AtualizarCliente } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, Icone } from '@/shared/ui';

export type CampoDeContato = 'telefone' | 'email' | 'documento';

const COMO_NO_CAMPO: Record<
  CampoDeContato,
  { rotulo: string; tipo: string; teclado?: 'tel' | 'email' | 'numeric'; maximo: number }
> = {
  telefone: { rotulo: 'telefone', tipo: 'tel', teclado: 'tel', maximo: 40 },
  email: { rotulo: 'e-mail', tipo: 'email', teclado: 'email', maximo: 200 },
  documento: { rotulo: 'documento', tipo: 'text', teclado: 'numeric', maximo: 30 },
};

/** O corpo do `PATCH`, ou `null` quando não há o que mandar. */
export function corpoDaEdicao(campo: CampoDeContato, texto: string): AtualizarCliente | null {
  const limpo = texto.trim();
  if (campo === 'documento') return limpo === '' ? null : { documento: limpo };
  return { [campo]: limpo === '' ? null : limpo } as AtualizarCliente;
}

export function ContatoEditavel({
  campo,
  clienteId,
  conversaId,
  valorAtual,
  podeEditar,
  children,
}: {
  campo: CampoDeContato;
  clienteId: string;
  conversaId: string;
  /** O que o campo traz escrito ao abrir. O documento sempre abre vazio. */
  valorAtual: string | null;
  podeEditar: boolean;
  /** Como a linha aparece fora da edição. */
  children: ReactNode;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const como = COMO_NO_CAMPO[campo];
  const [editando, definirEditando] = useState(false);
  const [texto, definirTexto] = useState('');
  /** Quantos cadastros o documento fez suspeitar — o aviso fica depois de salvar. */
  const [candidatos, definirCandidatos] = useState(0);

  const salvar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      const corpo = corpoDaEdicao(campo, texto);
      if (!corpo) return null;
      return api.atualizarCliente({ caminho: { id: clienteId }, corpo });
    },
    onSuccess: (salvo) => {
      // A conversa (este painel), o 360 e a fila, que mostra o nome e o contato.
      void cache.invalidateQueries({ queryKey: chaves.conversa(conversaId) });
      void cache.invalidateQueries({ queryKey: chaves.cliente360(clienteId) });
      void cache.invalidateQueries({ queryKey: chaves.inbox() });
      definirCandidatos(salvo?.candidatos_de_fusao ?? 0);
      definirEditando(false);
    },
  });

  const abrir = (): void => {
    definirTexto(campo === 'documento' ? '' : (valorAtual ?? ''));
    definirCandidatos(0);
    salvar.reset();
    definirEditando(true);
  };

  if (!editando) {
    return (
      <span className="painel-cliente__editavel">
        {children}
        {podeEditar && (
          <button
            type="button"
            className="painel-cliente__copiar"
            aria-label={`Editar ${como.rotulo}`}
            title={`Editar ${como.rotulo}`}
            onClick={abrir}
          >
            <Icone nome="lapis" />
          </button>
        )}
        {/*
          O documento pode revelar que dois cadastros são a mesma pessoa. Nada
          é unido sozinho: a frase diz onde a decisão está, para ninguém
          concluir que algo já foi feito (a Armadilha da C10).
        */}
        {candidatos > 0 && (
          <span className="painel-cliente__aviso" role="status">
            Documento salvo. Ele também está em outro cadastro: os dois foram para a revisão de
            identidade, e nada foi unido.
          </span>
        )}
      </span>
    );
  }

  return (
    <form
      className="painel-cliente__edicao"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!corpoDaEdicao(campo, texto)) {
          definirEditando(false);
          return;
        }
        salvar.mutate();
      }}
      onKeyDown={(evento) => {
        if (evento.key === 'Escape') definirEditando(false);
      }}
    >
      <Campo
        rotuloOculto={como.rotulo}
        tamanho="miudo"
        type={como.tipo}
        inputMode={como.teclado}
        maxLength={como.maximo}
        value={texto}
        // O foco vai para o campo que acabou de aparecer: quem clicou no lápis
        // vai digitar agora.
        autoFocus
        placeholder={campo === 'documento' ? 'CPF ou CNPJ' : undefined}
        ajuda={
          campo === 'documento'
            ? 'Guardado cifrado. Se já estiver em outro cadastro, os dois vão para a revisão.'
            : undefined
        }
        erro={salvar.isError ? recadoDeErro(salvar.error) : undefined}
        onChange={(evento) => definirTexto(evento.currentTarget.value)}
      />
      <span className="painel-cliente__edicao-acoes">
        <Botao
          variante="primario"
          miudo
          type="submit"
          desabilitado={salvar.isPending}
          motivo="Salvando."
        >
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </Botao>
        <Botao variante="fantasma" miudo type="button" onClick={() => definirEditando(false)}>
          Cancelar
        </Botao>
      </span>
    </form>
  );
}
